import React, { useEffect, useState } from 'react';
import { SelectValue } from 'antd/lib/select';
import { useTranslation } from 'react-i18next';
import { Row, Col } from 'antd';
import { ColumnType } from 'antd/lib/table';

import './index.less';

import {
  ProviderModel,
  ProviderModelOriginType,
  ProviderModelsEmbeddingItem,
  ProviderModelsLLMItem,
  ProviderModelsRerankItem,
  ProviderModelType,
  ProviderNames,
  ProviderSettingsOutput,
} from '../../../../../gamma-api';
import IbButton from '../../../../components/common/IbButton';
import IbInput from '../../../../components/common/IbInput';
import IbModal from '../../../../components/common/IbModal';
import IbSelect from '../../../../components/common/IbSelect';
import IbTypography from '../../../../components/common/IbTypography';
import {
  API_KEY_MASKED_LENGTH,
  API_KEY_VISIBLE_LENGTH,
  MODEL_PROVIDER_NAMES_OPTIONS,
  MODEL_TYPE_NAMES,
  MODEL_TYPES_OPTIONS,
  PROVIDER_SETTINGS_DEFAULTS,
} from '../const';
import IbIcon from '../../../../components/common/IbIcon';
import IbPopover from '../../../../components/common/IbPopover';
import IbTable from '../../../../components/common/IbTable';
import { useValidationForm } from '../../../../utils/validationUtil';
import { ValidationErrors } from '../../../../types';
import IbNumberInput from '../../../../components/common/IbNumberInput';
import { tryParseIntValue } from '../../../../utils/numberUtil';
import { mask, MaskingMode } from '../../../../utils/stringUtil';

const MAIN_CLASS_NAME = 'provider-edit-modal';
const FORM_ITEM_CLASS_NAME = `${MAIN_CLASS_NAME}__form-item`;
const SUBTITLE_CLASS_NAME = `${MAIN_CLASS_NAME}__subtitle`;
const ADD_POPOVER_CLASS_NAME = `${MAIN_CLASS_NAME}__add-popover`;
const ADD_POPOVER_ITEM_CLASS_NAME = `${ADD_POPOVER_CLASS_NAME}__item`;
const TABLE_CLASS_NAME = `${MAIN_CLASS_NAME}__table`;
const ACTIONS_CELL_CLASS_NAME = `${TABLE_CLASS_NAME}__actions-cell`;

const ACTIONS_DATA_INDEX = 'actions';

const getSettingsForType = (targetType: ProviderNames, currentSettings?: ProviderSettingsOutput) => {
  const defaults = PROVIDER_SETTINGS_DEFAULTS[targetType] || {};

  const commonValues = {
    provider_type: targetType,
    api_key: currentSettings?.api_key || '',
    models: currentSettings?.models || [],
  };

  const isSameType = currentSettings?.provider_type === targetType;
  const existingSpecificValues = isSameType ? currentSettings : {};

  return {
    ...defaults,
    ...existingSpecificValues,
    ...commonValues,
  };
};

type ProviderModelsItemBase = ProviderModelsLLMItem | ProviderModelsEmbeddingItem | ProviderModelsRerankItem;

type ProviderForm = {
  title: string | null | undefined;
  models: ProviderModelsItemBase[] | undefined;
};

export interface IProviderEditModalProps {
  visible: boolean;
  editProvider?: ProviderModel;
  onAdd: (provider: ProviderModel, onSubmitErrors: (errors: ValidationErrors) => void) => Promise<void>;
  onUpdate: (provider: ProviderModel, onSubmitErrors: (errors: ValidationErrors) => void) => Promise<void>;
  onCancel: () => void;
}

const ProviderEditModal: React.FC<IProviderEditModalProps> = ({ visible, editProvider, onAdd, onUpdate, onCancel }) => {
  const { t } = useTranslation();

  const initialValues = { title: editProvider?.title, models: editProvider?.settings.models };

  const checkFieldValueFnMap = { title: (value?: string | null) => !!value?.length, models: () => true };

  const {
    validate,
    validationState,
    reset,
    onFieldBlur,
    onFieldChange,
    onSubmitErrors,
    getShowError,
    getErrorMessage,
  } = useValidationForm<ProviderForm>(initialValues, checkFieldValueFnMap);

  const [saving, setSaving] = useState(false);
  const [provider, setProvider] = useState<ProviderModel>();
  const [addAdditionalModelPopoverVisible, setAddAdditionalModelPopoverVisible] = useState(false);
  const [additionalProviderModel, setAdditionalProviderModel] = useState<ProviderModelsItemBase>();
  const [apiKeyEditing, setApiKeyEditing] = useState(false);

  const isNewProvider = !editProvider?.provider_id;
  const isContextWindowInputVisible =
    additionalProviderModel?.type === ProviderModelType.Llm ||
    additionalProviderModel?.type === ProviderModelType.Embedding;

  const modalShowApiBase = 'api_base' in PROVIDER_SETTINGS_DEFAULTS[provider?.name || ProviderNames.Openai];
  const modalShowFolderId = 'folder_id' in PROVIDER_SETTINGS_DEFAULTS[provider?.name || ProviderNames.Openai];
  const modalShowScope = 'scope' in PROVIDER_SETTINGS_DEFAULTS[provider?.name || ProviderNames.Openai];

  const additionalModels =
    provider?.settings.models?.filter((m) => m.origin_type === ProviderModelOriginType.Custom) || [];

  const currentSettings = (provider?.settings as ProviderSettingsOutput) || {};

  useEffect(() => {
    reset(initialValues);
  }, [visible]);

  const onTitleChange = (value: string) => {
    setProvider({ ...provider, title: value } as ProviderModel);
    onFieldChange('title', value);
  };

  const onTitleBlur = () => onFieldBlur('title');

  const onNameChange = (value: SelectValue) => {
    const newType = value as ProviderNames;

    const newSettings = getSettingsForType(newType, provider?.settings);

    setProvider({
      ...provider,
      name: value,
      settings: newSettings,
    } as ProviderModel);
  };

  const onSettingsApiKeyChange = (value: string) =>
    setProvider({ ...provider, settings: { ...provider?.settings, api_key: value } } as ProviderModel);

  const onStartEditApiKeyClick = () => {
    setApiKeyEditing(true);
    setProvider({ ...provider, settings: { ...provider?.settings, api_key: '' } } as ProviderModel);
  };

  const onCancelEditApiKeyClick = () => {
    setApiKeyEditing(false);
    setProvider({
      ...provider,
      settings: { ...provider?.settings, api_key: null },
    } as ProviderModel);
  };

  const onSettingsApiBaseChange = (value: string) =>
    setProvider({ ...provider, settings: { ...provider?.settings, api_base: value } } as ProviderModel);

  const onSettingsFolderIdChange = (value: string) =>
    setProvider({ ...provider, settings: { ...provider?.settings, folder_id: value } } as ProviderModel);

  const onSettingsScopeChange = (value: string) =>
    setProvider({ ...provider, settings: { ...provider?.settings, scope: value } } as ProviderModel);

  const onAdditionalProviderModelNameChange = (value: string) =>
    setAdditionalProviderModel({ ...additionalProviderModel, name: value } as ProviderModelsItemBase);

  const onAdditionalProviderModelTypeChange = (value: SelectValue) =>
    setAdditionalProviderModel({
      name: additionalProviderModel?.name,
      type: value,
      origin_type: additionalProviderModel?.origin_type,
    } as ProviderModelsItemBase);

  const onAdditionalProviderModelContextWindowChange = (value: number | string) =>
    setAdditionalProviderModel({
      ...additionalProviderModel,
      context_window: tryParseIntValue(value),
    } as ProviderModelsItemBase);

  const onModalSaveButtonClick = async () => {
    if (!validate()) {
      return;
    }

    setSaving(true);
    provider && (await onUpdate(provider, onSubmitErrors));
    setSaving(false);
  };

  const onModalAddButtonClick = async () => {
    if (!validate()) {
      return;
    }

    setSaving(true);
    provider && (await onAdd(provider, onSubmitErrors));
    setSaving(false);
  };

  const onAddAdditionalModelButtonClick = () => {
    setAdditionalProviderModel({
      name: '',
      type: ProviderModelType.Llm,
      origin_type: ProviderModelOriginType.Custom,
    });
    setAddAdditionalModelPopoverVisible(true);
  };

  const onAddAdditionalModelPopoverVisibleChange = (visible?: boolean) => {
    if (visible) return;

    setAddAdditionalModelPopoverVisible(visible || false);
  };

  const onAdditionalProviderModelAddButtonClick = () => {
    if (!additionalProviderModel) {
      return;
    }
    const newModels = [...(provider?.settings.models || []), additionalProviderModel];
    setProvider({
      ...provider,
      settings: { ...provider?.settings, models: newModels },
    } as ProviderModel);
    setAddAdditionalModelPopoverVisible(false);
    onFieldChange('models', newModels);
  };

  const onAdditionalModelsClearButtonClick = () => {
    setProvider({
      ...provider,
      settings: { ...provider?.settings, models: [] },
    } as ProviderModel);
    onFieldChange('models', []);
  };

  const onAdditionalModelDelete = (model: ProviderModelsItemBase) => () => {
    const newModels = provider?.settings.models?.filter((m) => m !== model);
    setProvider({
      ...provider,
      settings: { ...provider?.settings, models: newModels || [] },
    } as ProviderModel);
    onFieldChange('models', newModels);
  };

  const onVisiblePropChange = () => {
    if (visible) {
      if (editProvider) {
        const hydratedSettings = getSettingsForType(editProvider.name, editProvider.settings);
        setProvider({
          ...editProvider,
          settings: {
            ...(hydratedSettings as ProviderSettingsOutput),
            api_key: null,
          },
        });
        reset({ title: editProvider.title, models: editProvider.settings.models });
      }
      setAddAdditionalModelPopoverVisible(false);
      setApiKeyEditing(false);
    }
  };
  useEffect(onVisiblePropChange, [visible, editProvider]);

  const columns: ColumnType<ProviderModelsItemBase>[] = [
    {
      title: t('Providers: Name'),
      render: (_, record) => <IbTypography.Paragraph type="secondary">{record.name}</IbTypography.Paragraph>,
    },
    {
      title: t('Providers: Model type'),
      render: (_, record: ProviderModelsItemBase) => (
        <IbTypography.Paragraph type="secondary">{MODEL_TYPE_NAMES[record.type]}</IbTypography.Paragraph>
      ),
    },
    {
      title: t('Providers: Context window'),
      render: (_, record: ProviderModelsItemBase) =>
        record.type === ProviderModelType.Llm || record.type === ProviderModelType.Embedding ? (
          <IbTypography.Paragraph type="secondary">
            {(record as ProviderModelsLLMItem).context_window}
          </IbTypography.Paragraph>
        ) : null,
    },
    {
      className: ACTIONS_CELL_CLASS_NAME,
      title: (
        <IbButton type="link" onClick={onAdditionalModelsClearButtonClick}>
          {t('Providers: Clear list')}
        </IbButton>
      ),
      render: (_, record) => (
        <IbButton icon={<IbIcon iconName="delete" />} type="icon" onClick={onAdditionalModelDelete(record)} />
      ),
      dataIndex: ACTIONS_DATA_INDEX,
    },
  ];

  const renderAddAdditionaModelPopoverContent = () => (
    <>
      <div className={ADD_POPOVER_ITEM_CLASS_NAME}>
        <IbTypography.Paragraph type="secondary">{t('Providers: Name')}</IbTypography.Paragraph>
        <IbInput value={additionalProviderModel?.name} onChange={onAdditionalProviderModelNameChange} />
      </div>
      <div className={ADD_POPOVER_ITEM_CLASS_NAME}>
        <IbTypography.Paragraph type="secondary">{t('Providers: Model type')}</IbTypography.Paragraph>
        <IbSelect
          options={MODEL_TYPES_OPTIONS}
          value={additionalProviderModel?.type}
          onChange={onAdditionalProviderModelTypeChange}
        />
      </div>
      {isContextWindowInputVisible && (
        <div className={ADD_POPOVER_ITEM_CLASS_NAME}>
          <IbTypography.Paragraph type="secondary">{t('Providers: Context window')}</IbTypography.Paragraph>
          <IbNumberInput
            min={0}
            precision={0}
            value={(additionalProviderModel as ProviderModelsLLMItem)?.context_window ?? undefined}
            onChange={onAdditionalProviderModelContextWindowChange}
          />
        </div>
      )}
      <IbButton type="fill" onClick={onAdditionalProviderModelAddButtonClick}>
        {t('Providers: Add')}
      </IbButton>
    </>
  );

  const renderModalFooter = () =>
    isNewProvider ? (
      <>
        <IbButton disabled={saving} onClick={onModalAddButtonClick}>
          {t('Providers: Add')}
        </IbButton>
        <IbButton type="secondary" onClick={onCancel}>
          {t('Providers: Cancel')}
        </IbButton>
      </>
    ) : (
      <>
        <IbButton disabled={saving} onClick={onModalSaveButtonClick}>
          {t('Providers: Save')}
        </IbButton>
        <IbButton type="secondary" onClick={onCancel}>
          {t('Providers: Cancel')}
        </IbButton>
      </>
    );

  return (
    <IbModal
      className={MAIN_CLASS_NAME}
      footer={renderModalFooter()}
      title={isNewProvider ? t('Providers: Add provider') : editProvider?.title}
      visible={visible}
      width="50%"
      onCancel={onCancel}
    >
      <div className={SUBTITLE_CLASS_NAME}>
        <IbTypography.Paragraph strong type="secondary">
          {t('Providers: Parameters')}
        </IbTypography.Paragraph>
      </div>
      <Row>
        <Col>
          <div className={FORM_ITEM_CLASS_NAME}>
            <IbTypography.Paragraph type="secondary">{t('Providers: Name')}</IbTypography.Paragraph>
            <IbInput
              status={getShowError(validationState.title) ? 'error' : 'default'}
              value={provider?.title}
              onBlur={onTitleBlur}
              onChange={onTitleChange}
            />
            {getShowError(validationState.title) && (
              <IbTypography.Paragraph error type="descriptor">
                {getErrorMessage(validationState.title, t('Providers: Name: Error'))}
              </IbTypography.Paragraph>
            )}
          </div>
        </Col>
        <Col>
          <div className={FORM_ITEM_CLASS_NAME}>
            <IbTypography.Paragraph type="secondary">{t('Providers: Provider')}</IbTypography.Paragraph>
            <IbSelect options={MODEL_PROVIDER_NAMES_OPTIONS} value={provider?.name} onChange={onNameChange} />
          </div>
        </Col>
      </Row>
      <Row>
        <Col>
          <div className={FORM_ITEM_CLASS_NAME}>
            <IbTypography.Paragraph type="secondary">{t('Providers: API Key')}</IbTypography.Paragraph>
            <IbInput
              disabled={!isNewProvider && !apiKeyEditing}
              suffix={
                !isNewProvider && (editProvider?.settings.api_key || apiKeyEditing) ? (
                  <IbButton
                    icon={<IbIcon iconName={apiKeyEditing ? 'close' : 'edit'} />}
                    type="icon"
                    onClick={apiKeyEditing ? onCancelEditApiKeyClick : onStartEditApiKeyClick}
                  />
                ) : undefined
              }
              value={
                !isNewProvider && !apiKeyEditing
                  ? mask(
                      editProvider?.settings.api_key,
                      MaskingMode.BeginEnd,
                      API_KEY_MASKED_LENGTH,
                      API_KEY_VISIBLE_LENGTH
                    )
                  : provider?.settings.api_key
              }
              onChange={onSettingsApiKeyChange}
            />
          </div>
        </Col>
        <Col>
          {modalShowApiBase && (
            <div className={FORM_ITEM_CLASS_NAME}>
              <IbTypography.Paragraph type="secondary">{t('Providers: Base URL')}</IbTypography.Paragraph>
              <IbInput value={(currentSettings as { api_base: string }).api_base} onChange={onSettingsApiBaseChange} />
            </div>
          )}
          {modalShowFolderId && (
            <div className={FORM_ITEM_CLASS_NAME}>
              <IbTypography.Paragraph type="secondary">{t('Providers: Folder ID')}</IbTypography.Paragraph>
              <IbInput
                value={(currentSettings as { folder_id: string }).folder_id}
                onChange={onSettingsFolderIdChange}
              />
            </div>
          )}
          {modalShowScope && (
            <div className={FORM_ITEM_CLASS_NAME}>
              <IbTypography.Paragraph type="secondary">{t('Providers: Scope')}</IbTypography.Paragraph>
              <IbInput value={(currentSettings as { scope: string }).scope} onChange={onSettingsScopeChange} />
            </div>
          )}
        </Col>
      </Row>
      <div className={SUBTITLE_CLASS_NAME}>
        <IbTypography.Paragraph strong error={getShowError(validationState.models)} type="secondary">
          {t('Providers: Additional models')}
        </IbTypography.Paragraph>
        <IbPopover
          className={ADD_POPOVER_CLASS_NAME}
          content={renderAddAdditionaModelPopoverContent()}
          placement="rightTop"
          trigger="click"
          visible={addAdditionalModelPopoverVisible}
          onVisibleChange={onAddAdditionalModelPopoverVisibleChange}
        >
          <IbButton icon={<IbIcon iconName="add-one" />} type="link" onClick={onAddAdditionalModelButtonClick} />
        </IbPopover>
      </div>
      {additionalModels.length ? (
        <IbTable className={TABLE_CLASS_NAME} columns={columns} dataSource={additionalModels} />
      ) : null}
      {getShowError(validationState.models) && (
        <IbTypography.Paragraph error type="descriptor">
          {getErrorMessage(validationState.models, t('Providers: Additional Models: Error'))}
        </IbTypography.Paragraph>
      )}
    </IbModal>
  );
};

export default ProviderEditModal;
